import { ICommandBarItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPMessageBar, IPMessageBarType, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import AuditCriteriaLayerHeader, {
  AuditCriteriaLayerCommand,
  AuditCriteriaLayerDialogs,
} from 'components/AuditCriteria/AuditCriteriaLayerHeader';
import { IEvidenceTaskLinkItem } from 'components/AuditCriteria/EvidenceTaskLinks';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import GenericModal from 'components/Dialogs/GenericModal';
import InfoMessage from 'components/Notification/InfoMessage';
import TemplateCriteriaImportPanel from 'components/AuditCriteria/TemplateCriteriaImportPanel';
import TaskTypeFormEditor from 'components/Tasks/Forms/TaskTypeFormEditor';
import { acceptIcon, archivedIcon, globalStackTokensGapMedium, templateIcon } from 'globalStyles';
import { projectCriteriaToFormElements } from 'mappings/auditCriteriaFormProjection';
import AuditCriteriaSet, {
  auditCriterionModeOneTime,
  auditCriterionStateActive,
  auditCriterionStateArchived,
  auditCriterionStateNotApplicable,
  auditEntityKeyString,
  getAuditCriteriaReviewReasonText,
  getAuditCriterionStateText,
} from 'models/auditCriteria';
import { AICriterionOutcomeResult, AICriterionOutcomeStatus, AuditCriteriaLayer } from 'models/auditCriteriaGeneric';
import { isAuditCriteriaPreviewEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAuditCriteria, apiSetAuditCriteriaReviewed } from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { toLocaleDateShort } from 'utils/datetime';

/**
 * Where these criteria are shown. 'modal' (the default) is the window of its own: title, subtitle, a
 * Close button and a fixed height the form scrolls inside. 'wizard' is the body alone, flowing to its
 * natural height - the bulk-review panel owns the chrome, the scrolling and the navigation. There the
 * close button and the "not reviewed yet" warning are left out: every item of that walk is unreviewed
 * or unverified by construction, and the way out of a step is Reviewed or Next. 'embedded' flows the
 * same way but shows the criteria the way a procedure's FORM tab will once the form exists: Add and
 * the AI actions only - no Reviewed, no Show archived, no state line and no out-of-sync warning,
 * which are the review surface's concerns, not the form's. 'generic' is a generic set in the generic criteria modal
 * (PLAN-075), read and written under its row key (the family, and 0 or -(subType + 1)): the modal's body without its
 * chrome, filling the host's height the way the modal does, with every review command.
 */
export type AuditCriteriaEditorUsageMode = 'modal' | 'wizard' | 'embedded' | 'generic';

interface IAuditCriteriaEditorModalProps {
  isOpen: boolean;
  entityType: number;
  entityId: number;
  // The entity's display name, for the modal title.
  entityName?: string;
  readonly?: boolean;
  // false: the editor's reads start no re-evaluation of a flagged set ("Show criteria" of the Assess menu)
  queueReevaluation?: boolean;
  usageMode?: AuditCriteriaEditorUsageMode;
  onClose: () => void;
  // Fired after any write, so the host can refresh stats or reload a projected form.
  onChanged?: (set: AuditCriteriaSet) => void;
  // A review was WRITTEN (ticked, not withdrawn): the bulk-review panel moves to its next item on it.
  onReviewed?: () => void;
  // The review write is in flight, so a host with navigation of its own can lock it: an item must not
  // be left, and this editor unmounted, while its review is still being written.
  onBusyChange?: (isBusy: boolean) => void;
}

// Criteria writes update the shared set and reload its local form projection.
const AuditCriteriaEditorModal = (props: IAuditCriteriaEditorModalProps) => {
  const { t } = useTranslation(['criteria', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  // PLAN-075's parts of this component are a preview (isAuditCriteriaPreviewEnabled)
  const auditPreview = isAuditCriteriaPreviewEnabled(appContext);

  const [criteriaSet, setCriteriaSet] = useState<AuditCriteriaSet | undefined>(undefined);
  const [formElements, setFormElements] = useState<TaskTypeFormElement[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // The criteria shown: the active ones (the set the audits ask), or the not applicable or the archived ones.
  const [stateFilter, setStateFilter] = useState<string>(auditCriterionStateActive);
  const [isReviewing, setIsReviewing] = useState<boolean>(false);
  // Which question is on screen: ticking Reviewed while the evidence is not verified, or withdrawing
  // a review. Undefined while neither is asked.
  const [confirmReview, setConfirmReview] = useState<'review' | 'unreview' | undefined>(undefined);

  const usageMode = props.usageMode ?? 'modal';
  const isModal = usageMode === 'modal';
  const isEmbedded = usageMode === 'embedded';
  // the modal and the generic set's pane have a fixed height the form scrolls inside
  const fillsHost = isModal || usageMode === 'generic';
  // an item's criteria (not a generic set's own pane): the header names where they come from (PLAN-075)
  const showsLayer = isModal || usageMode === 'wizard';
  const [layerCommand, setLayerCommand] = useState<AuditCriteriaLayerCommand | undefined>(undefined);
  const [isLayerBusy, setIsLayerBusy] = useState<boolean>(false);
  // "Add from template" on a generic set (PLAN-CTI-081)
  const [showTemplateImport, setShowTemplateImport] = useState<boolean>(false);

  const applyResult = useCallback(
    (set: AuditCriteriaSet | undefined, notify: boolean) => {
      setCriteriaSet(set);
      //only a WRITE tells the host something changed: the first read of the set changed nothing
      if (set && notify) props.onChanged?.(set);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.entityType, props.entityId],
  );

  //every criteria command writes to the set server-side (fork, retire, re-project), so the editor's
  //elements are re-read from the server rather than patched here
  const load = useCallback(
    async (afterChange: boolean) => {
      try {
        setIsLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const set = await apiGetAuditCriteria(
          props.entityType,
          props.entityId,
          accessToken,
          props.queueReevaluation ?? true,
        );
        applyResult(set, afterChange);
      } catch (err) {
        // 404 = the entity has no criteria yet, which the empty state below explains
        const appErr = err as AppError;
        if (appErr?.debug?.includes('404')) {
          applyResult(undefined, false);
        } else {
          appContext.setError(err);
        }
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.entityType, props.entityId, props.queueReevaluation, applyResult],
  );

  useEffect(() => {
    if (!props.isOpen) return;
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.entityType, props.entityId]);

  useEffect(() => {
    props.onBusyChange?.(isReviewing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReviewing]);

  // The server started a re-evaluation of this set on the read (a flagged set): the set is read again
  // every 3 seconds until the stamp lands, for at most 90 seconds. After that the editor shows what it
  // has; the next open picks the result up.
  const [reevaluationTimedOut, setReevaluationTimedOut] = useState<boolean>(false);
  const isWaitingForReevaluation = criteriaSet?.reevaluating === true && !reevaluationTimedOut;

  useEffect(() => {
    setReevaluationTimedOut(false);
  }, [props.entityType, props.entityId]);

  useEffect(() => {
    if (!props.isOpen || !isWaitingForReevaluation) return;

    const startedOn = Date.now();
    let cancelled = false;
    const timer = window.setInterval(() => {
      void (async () => {
        try {
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          const set = await apiGetAuditCriteria(
            props.entityType,
            props.entityId,
            accessToken,
            props.queueReevaluation ?? true,
          );
          if (cancelled) return;
          if (!set.reevaluating) {
            //the re-evaluation wrote the set: the host refreshes what it shows of it
            applyResult(set, true);
          } else if (Date.now() - startedOn >= 90000) {
            setReevaluationTimedOut(true);
          }
        } catch (err) {
          if (!cancelled) {
            setReevaluationTimedOut(true);
            appContext.setError(err);
          }
        }
      })();
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, isWaitingForReevaluation, props.entityType, props.entityId]);

  // A control with child items carries no criteria: one message replaces the form and its commands.
  const isParentNode = criteriaSet?.isLeaf === false;
  const hasCriteria = (criteriaSet?.criteria ?? []).length > 0;
  const countInState = (state: string): number =>
    (criteriaSet?.criteria ?? []).filter((criterion) => criterion.state === state).length;
  const filteredCount = countInState(stateFilter);

  useEffect(() => {
    if (stateFilter !== auditCriterionStateActive && filteredCount === 0) setStateFilter(auditCriterionStateActive);
  }, [stateFilter, filteredCount]);

  // The filter switches the list over to one state rather than adding to the active one: a not
  // applicable or archived criterion states no live obligation, so mixing them reads as one longer set
  // of criteria the auditee has to answer.
  useEffect(() => {
    const criteria = (criteriaSet?.criteria ?? []).filter((criterion) => criterion.state === stateFilter);
    setFormElements(projectCriteriaToFormElements(criteria, props.entityType, props.entityId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteriaSet, stateFilter]);

  // One audited item, named: what makes the renderer draw the section heading the criteria commands
  // hang on, and the accent block around this item's questions.
  const entityNamesByKey = useMemo(
    () => new Map<string, string>([[auditEntityKeyString(props.entityType, props.entityId), props.entityName ?? '']]),
    [props.entityType, props.entityId, props.entityName],
  );

  // The evidence tasks a one-time criterion already has, with their state, so the card colours its
  // badge the way the overview does. The cards do not list them: the criterion editor, one click
  // away, shows the tasks with their state and the AI advice.
  const evidenceTasksByCriterionKey = useMemo(() => {
    const byCriterionKey = new Map<string, IEvidenceTaskLinkItem[]>();

    (criteriaSet?.criteria ?? []).forEach((criterion) => {
      const tasks = criterion.evidenceTasks
        .filter((binding) => binding.mode === auditCriterionModeOneTime)
        .map((binding) => ({
          taskId: binding.taskId,
          taskName: binding.taskName,
          taskClosed: binding.taskClosed,
        }));
      if (tasks.length > 0) byCriterionKey.set(criterion.criterionKey.toLowerCase(), tasks);
    });

    return byCriterionKey;
  }, [criteriaSet]);

  // An item on a generic set reads that set's criteria: they are edited and reviewed in the generic set, or in the
  // item's own copy ("Change for this item").
  const isInherited =
    showsLayer &&
    (criteriaSet?.layer === AuditCriteriaLayer.Family || criteriaSet?.layer === AuditCriteriaLayer.Subtype);
  const isReadonly = !!props.readonly || isInherited;

  const getOutcomeBadgeText = (result: AICriterionOutcomeResult): string => {
    switch (result) {
      case AICriterionOutcomeResult.Pass:
        return t('criteria:Generic.Outcome.BadgePass');
      case AICriterionOutcomeResult.Fail:
        return t('criteria:Generic.Outcome.BadgeFail');
      default:
        return t('criteria:Generic.Outcome.BadgeInconclusive');
    }
  };

  // The cards' latest AI outcome pill (PLAN-075, spec 6.2), from the set's outcomes, which are kept by the criterion's
  // lineage.
  const criterionBadgesByKey = useMemo(() => {
    const byCriterionKey = new Map<string, string[]>();
    if (!criteriaSet) return byCriterionKey;

    criteriaSet.criteria.forEach((criterion) => {
      const lineage = (criterion.lineageKey || criterion.criterionKey).toLowerCase();
      const badges: string[] = [];
      const outcome = criteriaSet.latestOutcomes.find(
        (item) =>
          item.lineageKey.toLowerCase() === lineage &&
          item.status !== AICriterionOutcomeStatus.Dismissed &&
          item.status !== AICriterionOutcomeStatus.Superseded,
      );
      if (outcome) badges.push(getOutcomeBadgeText(outcome.proposedResult));
      if (badges.length > 0) byCriterionKey.set(criterion.criterionKey.toLowerCase(), badges);
    });

    return byCriterionKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteriaSet]);

  const isReviewed = criteriaSet?.isReviewed() === true;
  // a generation input changed after the last review: said in place of the plain "not reviewed" bar,
  // in the modal and in the wizard alike (the wizard walks these too)
  const needsReview = criteriaSet?.needsReview() === true;
  // the AI changed the set after its last review: this banner says what changed and why, in place of
  // the needs-review and out-of-sync warnings, which the re-evaluation answered
  const isReevaluated = criteriaSet?.isReevaluatedSinceReview() === true;

  const getUserName = (userId: string | undefined): string | undefined => {
    if (!userId) return undefined;
    const user = appContext.globalDataCache.users.get(userId);

    return user?.name ? user.name : undefined;
  };

  // What the set is at this moment, in one line: reviewed and by whom, or - because editing and
  // reviewing are separate acts - who last changed it while it waits for a review.
  const getStateText = (): string | undefined => {
    if (!criteriaSet?.hasCriteria) return undefined;
    if (isReviewed && criteriaSet.reviewedOn) {
      const user = getUserName(criteriaSet.reviewedById);

      return user
        ? t('criteria:Editor.State.ReviewedBy', { date: toLocaleDateShort(criteriaSet.reviewedOn), user: user })
        : t('criteria:Editor.State.Reviewed', { date: toLocaleDateShort(criteriaSet.reviewedOn) });
    }
    if (criteriaSet.modifiedOn) {
      const user = getUserName(criteriaSet.modifiedById);

      return user
        ? t('criteria:Editor.State.ModifiedBy', { date: toLocaleDateShort(criteriaSet.modifiedOn), user: user })
        : t('criteria:Editor.State.Modified', { date: toLocaleDateShort(criteriaSet.modifiedOn) });
    }
    if (criteriaSet.generatedOn) {
      return t('criteria:Editor.State.Generated', { date: toLocaleDateShort(criteriaSet.generatedOn) });
    }

    return undefined;
  };

  // The review is its own write: it stamps who read the criteria and when, and changes nothing about
  // the criteria themselves. Unchecking withdraws it again.
  const setReviewed = async (reviewed: boolean) => {
    let saved = false;

    try {
      setIsReviewing(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const set = await apiSetAuditCriteriaReviewed(props.entityType, props.entityId, reviewed, accessToken);
      applyResult(set, true);
      saved = true;
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsReviewing(false);
    }

    //told only once the write is done and the busy flag is down: the host may answer it by moving to
    //its next item, which unmounts this editor
    if (saved && reviewed) props.onReviewed?.();
  };

  // Reviewed and verified are two independent facts: Reviewed says "I read these criteria and agree
  // with them", verified says a person closed the evidence task that proves a one-time criterion.
  // Ticking Reviewed while the evidence is not in is allowed, but worth a second look - and taking a
  // review back always is, because it puts the item back on everybody's list.
  const requestReview = () => {
    if (isReviewed) {
      setConfirmReview('unreview');

      return;
    }
    if (criteriaSet && !criteriaSet.isEvidenceVerified()) {
      setConfirmReview('review');

      return;
    }

    void setReviewed(true);
  };

  // The state shown and its count name the command; the menu offers each state that has criteria.
  const stateFilterLabel = (state: string): string =>
    t('criteria:State.FilterOption', { state: getAuditCriterionStateText(state, t), n: countInState(state) });

  const criteriaCommandBarItems: ICommandBarItemProps[] = [
    {
      key: 'stateFilter',
      text: stateFilterLabel(stateFilter),
      checked: stateFilter !== auditCriterionStateActive,
      iconProps: archivedIcon,
      disabled: isLoading || !hasCriteria,
      subMenuProps: {
        items: [auditCriterionStateActive, auditCriterionStateNotApplicable, auditCriterionStateArchived].map(
          (state) => ({
            key: state,
            text: stateFilterLabel(state),
            canCheck: true,
            checked: state === stateFilter,
            disabled: state !== auditCriterionStateActive && countInState(state) === 0,
            onClick: () => setStateFilter(state),
          }),
        ),
      },
    },
  ];

  if (hasCriteria) {
    criteriaCommandBarItems.push({
      key: 'reviewed',
      text: t('criteria:Editor.Reviewed'),
      title: t('criteria:Editor.ReviewedInfo'),
      checked: isReviewed,
      iconProps: acceptIcon,
      disabled: isLoading || isReviewing,
      onClick: () => requestReview(),
    });
  }

  // a generic set only: the set is in hand and its editor is the manager's
  const templateImportSetId = usageMode === 'generic' ? criteriaSet?.auditCriteriaSetId : undefined;
  if (templateImportSetId) {
    criteriaCommandBarItems.push({
      key: 'addFromTemplate',
      text: t('criteria:TemplateImport.Command'),
      iconProps: templateIcon,
      disabled: isLoading,
      onClick: () => setShowTemplateImport(true),
    });
  }

  //
  // Main render
  //
  /* The state rows stay put and the form below them is the only thing that scrolls (its
     ScrollableStackItem), which is what keeps the modal's title and footer in view. Nothing is pinned
     in the wizard: there the form flows and the panel scrolls the lot. */
  const body = (
    <IPStack verticalFill={fillsHost} styles={{ root: { minHeight: fillsHost ? 0 : undefined } }}>
      {/* the modal's own rule under its header; the panel header brings one */}
      {isModal && <IPSeparator />}
      <IPStack
        verticalFill={fillsHost}
        tokens={globalStackTokensGapMedium}
        styles={{ root: { paddingTop: 12, minHeight: fillsHost ? 0 : undefined } }}
      >
        {showsLayer && auditPreview && criteriaSet && !isLoading && (
          <IPStack.Item>
            <AuditCriteriaLayerHeader
              criteriaSet={criteriaSet}
              entityType={props.entityType}
              readonly={props.readonly}
              disabled={isLayerBusy}
              onCommand={setLayerCommand}
            />
          </IPStack.Item>
        )}
        {isWaitingForReevaluation && (
          <IPStack.Item>
            <IPSpinner size={IPSpinnerSize.small} label={t('criteria:Editor.Progress.Syncing')} labelPosition="right" />
          </IPStack.Item>
        )}
        {!isEmbedded && isReevaluated && !isLoading && (
          <IPStack.Item>
            <IPMessageBar messageBarType={IPMessageBarType.warning}>
              {t('criteria:Editor.Reevaluated', {
                date: toLocaleDateShort(criteriaSet?.reevaluatedOn),
                reason: getAuditCriteriaReviewReasonText(criteriaSet?.reevaluatedReason, t),
                added: criteriaSet?.changes.added ?? 0,
                reworded: criteriaSet?.changes.reworded ?? 0,
                archived: criteriaSet?.changes.archived ?? 0,
              })}
            </IPMessageBar>
          </IPStack.Item>
        )}
        {!isEmbedded && !isReevaluated && criteriaSet?.outOfSync && (
          <IPStack.Item>
            <IPMessageBar messageBarType={IPMessageBarType.warning}>{t('criteria:Editor.OutOfSync')}</IPMessageBar>
          </IPStack.Item>
        )}
        {/* Left out in the wizard: it walks through unreviewed and unverified items, so the warning
            would sit on every step. The state line below still says who changed what, and when. */}
        {!isEmbedded && !isReevaluated && hasCriteria && needsReview && !isLoading && (
          <IPStack.Item>
            <IPMessageBar messageBarType={IPMessageBarType.warning}>
              {t('criteria:Editor.ReviewRequired', {
                reason: getAuditCriteriaReviewReasonText(criteriaSet?.reviewRequiredReason, t),
                date: toLocaleDateShort(criteriaSet?.reviewRequiredOn),
              })}
            </IPMessageBar>
          </IPStack.Item>
        )}
        {!isEmbedded && getStateText() && (
          <IPStack.Item>
            <InfoMessage message={getStateText() ?? ''} />
          </IPStack.Item>
        )}
        {!hasCriteria && !isLoading && isReadonly && (
          <IPStack.Item>
            <IPText>{t('criteria:Editor.NoCriteria')}</IPText>
          </IPStack.Item>
        )}
        <IPStack.Item grow styles={{ root: { minHeight: fillsHost ? 0 : undefined } }}>
          {/* height 100%: the renderer inside fills its host, see TaskTypeFormEditor. The wizard has
              no fixed height to fill, so there the form is left to flow to its own. */}
          <div style={{ height: fillsHost ? '100%' : undefined }}>
            <TaskTypeFormEditor
              taskTypeId={0}
              isSystemForm={false}
              kpis={[]}
              formElements={formElements}
              editable={!isReadonly}
              readonly={isReadonly}
              isLoading={isLoading}
              compact={true}
              designTime={true}
              showFieldCards={true}
              criteriaOnly={true}
              showRetired={stateFilter !== auditCriterionStateActive}
              hideEmptyFormText={true}
              disableScrollContainer={!fillsHost}
              // Criterion editors use modals because panels render behind this modal's layer.
              fieldPanelAsPanel={false}
              formFieldModalAsPanel={false}
              entityNamesByKey={entityNamesByKey}
              // a generic set has no evidence tasks (they are per item): without the map the badges carry no evidence state
              evidenceTasksByCriterionKey={usageMode === 'generic' ? undefined : evidenceTasksByCriterionKey}
              criterionBadgesByKey={auditPreview ? criterionBadgesByKey : undefined}
              criteriaCommandBarItems={!isReadonly && !isEmbedded ? criteriaCommandBarItems : undefined}
              onElementsChange={(next) => setFormElements(next)}
              onCriteriaChanged={() => void load(true)}
            />
          </div>
        </IPStack.Item>
      </IPStack>
    </IPStack>
  );

  const parentNodeBody = (
    <IPStack>
      {isModal && <IPSeparator />}
      <IPStack styles={{ root: { paddingTop: 12 } }}>
        <IPStack.Item>
          <IPMessageBar messageBarType={IPMessageBarType.info}>{t('criteria:Editor.ParentNode')}</IPMessageBar>
        </IPStack.Item>
      </IPStack>
    </IPStack>
  );

  const reviewDialog = confirmReview ? (
    <DialogYesNo
      hidden={false}
      title={
        confirmReview === 'review'
          ? t('criteria:Editor.ReviewUnverifiedTitle')
          : t('criteria:Editor.UnreviewConfirmTitle')
      }
      subText={
        confirmReview === 'review' ? t('criteria:Editor.ReviewUnverified') : t('criteria:Editor.UnreviewConfirm')
      }
      onYes={() => {
        const reviewed = confirmReview === 'review';
        setConfirmReview(undefined);
        void setReviewed(reviewed);
      }}
      onNo={() => setConfirmReview(undefined)}
    />
  ) : null;

  const layerDialogs = (
    <AuditCriteriaLayerDialogs
      command={layerCommand}
      entityType={props.entityType}
      entityId={props.entityId}
      entityName={props.entityName}
      onBusyChange={setIsLayerBusy}
      onChanged={(set) => applyResult(set, true)}
      onClose={() => setLayerCommand(undefined)}
    />
  );

  const templateImportDialog = templateImportSetId ? (
    <TemplateCriteriaImportPanel
      isOpen={showTemplateImport}
      auditCriteriaSetId={templateImportSetId}
      targetName={props.entityName ?? ''}
      onClose={() => setShowTemplateImport(false)}
      // re-read as after every criteria write: the server re-projected the set form
      onImported={() => void load(true)}
    />
  ) : null;

  if (!isModal) {
    return (
      <>
        {isParentNode ? parentNodeBody : body}
        {reviewDialog}
        {layerDialogs}
        {templateImportDialog}
      </>
    );
  }

  return (
    <>
      <GenericModal
        isOpen={props.isOpen}
        isBlocking
        onClose={props.onClose}
        title={props.entityName ? `${t('criteria:Editor.Title')} - ${props.entityName}` : t('criteria:Editor.Title')}
        subTitle={t('criteria:Editor.SubTitle')}
        hideFooter={false}
        closeButtonText={t('criteria:Close')}
        isActionPending={isLoading || isReviewing}
        height={720}
        width={900}
        maxWidth={'min(90vw, 900px)'}
        maxHeight={'90vh'}
      >
        {isParentNode ? parentNodeBody : body}
      </GenericModal>
      {reviewDialog}
      {layerDialogs}
    </>
  );
};

export default AuditCriteriaEditorModal;
